import type { Unit } from '@/types/unit'

export type ConversionResult = {
  value: number
  fromUnit: string
  toUnit: string
}

export class UnitConverter {
  private units: Map<string, Unit>

  constructor(units: Unit[]) {
    this.units = new Map(units.map((unit) => [unit.name, unit]))
  }

  convertImperialToMetric(unitName: string, value: number): ConversionResult | null {
    const unit = this.units.get(unitName)
    if (!unit) return null

    return {
      value: value * unit.conversion_i_m,
      fromUnit: unit.units_i,
      toUnit: unit.units_m,
    }
  }

  convertMetricToImperial(unitName: string, value: number): ConversionResult | null {
    const unit = this.units.get(unitName)
    if (!unit) return null

    return {
      value: value * unit.conversion_m_i,
      fromUnit: unit.units_m,
      toUnit: unit.units_i,
    }
  }

  convert(
    fromUnitName: string,
    toUnitName: string,
    value: number,
    fromSystem: 'imperial' | 'metric',
  ): number | null {
    const fromUnit = this.units.get(fromUnitName)
    const toUnit = this.units.get(toUnitName)

    if (!fromUnit || !toUnit) return null

    if (fromUnitName === toUnitName) {
      if (fromSystem === 'imperial') {
        return value * fromUnit.conversion_i_m
      }
      return value * fromUnit.conversion_m_i
    }

    return null
  }

  getAllUnits(): Unit[] {
    return Array.from(this.units.values())
  }

  getUnitsByCategory(): Map<string, Unit[]> {
    const categories = new Map<string, Unit[]>()

    for (const unit of this.units.values()) {
      const baseUnit = this.getBaseUnitType(unit.units_m)
      if (!categories.has(baseUnit)) {
        categories.set(baseUnit, [])
      }
      categories.get(baseUnit)!.push(unit)
    }

    return categories
  }

  private getBaseUnitType(unitString: string): string {
    if (unitString.includes('tons')) {
      return 'mass'
    }
    if (unitString.includes('MPa') || unitString.includes('Pa')) {
      return 'pressure'
    }
    if (unitString.includes('kN')) {
      if (unitString.includes('-m')) {
        if (unitString.includes('/m')) return 'moment_per_length'
        return 'moment'
      }
      if (unitString.includes('/m')) return 'force_per_length'
      return 'force'
    }
    if (unitString.includes('kg')) {
      if (unitString.includes('/m^2')) return 'weight_per_area'
      if (unitString.includes('/m')) return 'weight_per_length'
      return 'weight'
    }
    if (
      unitString.includes('mm') ||
      unitString.includes('cm') ||
      (unitString.includes('m') && !unitString.includes('tons'))
    ) {
      if (unitString.includes('^2')) {
        if (unitString.includes('/')) return 'area_per_length'
        return 'area'
      }
      if (unitString.includes('^3')) {
        if (unitString.includes('/')) return 'volume_per_length'
        return 'volume'
      }
      if (unitString.includes('^4')) {
        if (unitString.includes('/')) return 'moment_of_inertia_per_length'
        return 'moment_of_inertia'
      }
      return 'length'
    }
    return 'other'
  }

  static formatValue(value: number, precision: number = 6): string {
    return formatNumber(value, precision)
  }
}

/**
 * Format a number stripping trailing zeros: 25.00 → "25", 25.10 → "25.1", 25.12 → "25.12"
 */
export function formatNumber(value: number, precision: number = 2): string {
  // Only zeros after the decimal point: with precision 0 there is none, and
  // 1990 must not lose its own.
  return value
    .toFixed(precision)
    .replace(/(\.\d*?)0+$/, '$1')
    .replace(/\.$/, '')
}

export function createConversionFunctions(units: Unit[]) {
  const converter = new UnitConverter(units)

  return {
    inchesToMm: (inches: number) => converter.convertImperialToMetric('width', inches)?.value ?? 0,
    mmToInches: (mm: number) => converter.convertMetricToImperial('width', mm)?.value ?? 0,
    feetToMeters: (feet: number) => converter.convertImperialToMetric('length', feet)?.value ?? 0,
    metersToFeet: (meters: number) =>
      converter.convertMetricToImperial('length', meters)?.value ?? 0,

    sqInToSqMm: (sqIn: number) => converter.convertImperialToMetric('area', sqIn)?.value ?? 0,
    sqMmToSqIn: (sqMm: number) => converter.convertMetricToImperial('area', sqMm)?.value ?? 0,
    sqFtToSqM: (sqFt: number) => converter.convertImperialToMetric('wall_area', sqFt)?.value ?? 0,
    sqMToSqFt: (sqM: number) => converter.convertMetricToImperial('wall_area', sqM)?.value ?? 0,

    lbToKg: (lb: number) => converter.convertImperialToMetric('raw_weight', lb)?.value ?? 0,
    kgToLb: (kg: number) => converter.convertMetricToImperial('raw_weight', kg)?.value ?? 0,
    lbPerFtToKgPerM: (lbPerFt: number) =>
      converter.convertImperialToMetric('weight', lbPerFt)?.value ?? 0,
    kgPerMToLbPerFt: (kgPerM: number) =>
      converter.convertMetricToImperial('weight', kgPerM)?.value ?? 0,

    kipFtToKNm: (kipFt: number) =>
      converter.convertImperialToMetric('design_moment', kipFt)?.value ?? 0,
    kNmToKipFt: (kNm: number) =>
      converter.convertMetricToImperial('design_moment', kNm)?.value ?? 0,

    ksiToMPa: (ksi: number) => converter.convertImperialToMetric('pressure', ksi)?.value ?? 0,
    mPaToKsi: (mPa: number) => converter.convertMetricToImperial('pressure', mPa)?.value ?? 0,

    convert: (unitName: string, value: number, direction: 'toMetric' | 'toImperial') => {
      if (direction === 'toMetric') {
        return converter.convertImperialToMetric(unitName, value)
      }
      return converter.convertMetricToImperial(unitName, value)
    },

    getConverter: () => converter,
  }
}

// Standalone wall-length conversion (no API-provided unit table needed).
// Factor mirrors lib/units.rb.
const M_IN_FT = 3.2808399

/**
 * Converts a wall dimension typed by the user when the units toggle flips.
 * Keeps the string empty/invalid input untouched so the input stays editable.
 */
export const convertWallDimension = (
  value: string,
  from: 'm' | 'i',
  to: 'm' | 'i',
  decimals = 2,
): string => {
  if (from === to || value.trim() === '') return value
  const parsed = Number(value)
  if (Number.isNaN(parsed)) return value

  const converted = to === 'i' ? parsed * M_IN_FT : parsed / M_IN_FT
  return String(Number(converted.toFixed(decimals)))
}
